import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@shadcn-in-nx/ui/button";
import { Input } from "@shadcn-in-nx/ui/input";
import { Loader2, Play } from "lucide-react";
import AutomationFields from "./AutomationFields";

const AGENT_TYPES = [
    { value: "automation", labelKey: "admin.automationPlayground.agents.automation" },
    { value: "search-apis", labelKey: "admin.automationPlayground.agents.searchApis" },
    { value: "answer-platform-question", labelKey: "admin.automationPlayground.agents.platformQA" },
];

export default function PlaygroundInput({ onRun, isLoading }) {
    const { t } = useTranslation();
    const [agentType, setAgentType] = useState("automation");
    const [workflowId, setWorkflowId] = useState("");
    const [stepId, setStepId] = useState("");
    const [message, setMessage] = useState("");

    const isAutomation = agentType === "automation";
    const isSearchApis = agentType === "search-apis";
    const isPlatformQA = agentType === "answer-platform-question";
    const messageKey = isSearchApis ? "objectivePlaceholder" : isPlatformQA ? "questionPlaceholder" : "messagePlaceholder";
    const messageLabel = isSearchApis ? "objective" : isPlatformQA ? "question" : "message";
    const canSubmit = isAutomation ? (workflowId && message) : message;

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!canSubmit) return;
        if (isAutomation) {
            onRun({ agentType, workflowId, stepId: stepId || undefined, message });
        } else if (isSearchApis) {
            onRun({ agentType, objective: message, companyId: workflowId || undefined });
        } else {
            onRun({ agentType, question: message, context: stepId || undefined });
        }
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-3 bg-slate-50 p-4 rounded-lg border">
            <div>
                <label className="text-sm font-medium text-slate-700">{t("admin.automationPlayground.agentType")}</label>
                <div className="mt-1 flex gap-2">
                    {AGENT_TYPES.map((a) => (
                        <button key={a.value} type="button" onClick={() => setAgentType(a.value)}
                            className={`px-3 py-1.5 text-sm rounded-md border transition-colors ${
                                agentType === a.value
                                    ? "bg-slate-900 text-white border-slate-900"
                                    : "bg-white text-slate-700 border-slate-300 hover:bg-slate-100"
                            }`}>{t(a.labelKey)}</button>
                    ))}
                </div>
            </div>
            {isAutomation && <AutomationFields workflowId={workflowId} setWorkflowId={setWorkflowId} stepId={stepId} setStepId={setStepId} />}
            {isPlatformQA && (
                <div>
                    <label className="text-sm font-medium text-slate-700">{t("admin.automationPlayground.context")}</label>
                    <Input value={stepId} onChange={(e) => setStepId(e.target.value)}
                        placeholder={t("admin.automationPlayground.contextPlaceholder")} className="mt-1" />
                </div>
            )}
            <div>
                <label className="text-sm font-medium text-slate-700">{t(`admin.automationPlayground.${messageLabel}`)}</label>
                <textarea value={message} onChange={(e) => setMessage(e.target.value)}
                    placeholder={t(`admin.automationPlayground.${messageKey}`)} rows={3}
                    className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
            </div>
            <Button type="submit" disabled={isLoading || !canSubmit}>
                {isLoading
                    ? <><Loader2 className="h-4 w-4 animate-spin mr-2" /> {t("common.loading")}</>
                    : <><Play className="h-4 w-4 mr-2" /> {t("admin.automationPlayground.run")}</>}
            </Button>
        </form>
    );
}
